import React, { useState, useEffect, useCallback } from 'react';
import { ShieldAlert, Copy, Check, CheckCircle, RefreshCw, AlertTriangle, Globe, Clock, Terminal, CloudDownload } from 'lucide-react';
import { getSunoCredits } from '@services/sunoApi';
import { Button } from '../ui/Button';
import { Input, TextArea, Select } from '../ui/Form';
import { Badge } from '../ui/Badge';
import { copyToClipboard } from '@/utils/clipboard';
import { useUserSettings } from '@hooks/useUserSettings';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import { SUNO_GENERATION_MODELS, resolveSunoGenerationModel } from '@/shared/sunoModels';
import { SunoModelsPanel } from './SunoModelsPanel';

interface SunoSyncEngineProps {
  sunoCookie: string;
  onSave: (settings: { sunoCookie: string; sunoProxyUrl: string }) => void;
}

const EXTRACTION_SCRIPT = `(async function() {
  try {
    const token = await window.Clerk.session.getToken();
    if (token) {
      console.log("%c Suno Bearer Token Found! ", "background: rgb(var(--color-accent)); color: #000; font-size: 14px; font-weight: bold;");
      console.log(token);
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(token);
        } else if (typeof copy === 'function') {
          copy(token);
        } else {
          const ta = document.createElement('textarea');
          ta.value = token;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        alert("Bearer Token copied to clipboard! Paste it into HookHouse Pro.");
      } catch (copyErr) {
        prompt("Auto-copy failed. Manually copy the token below:", token);
      }
    } else {
      throw new Error("No token returned");
    }
  } catch (e) {
    console.error("Could not find Clerk session. Are you logged in at suno.com?");
    alert("Token not found. Make sure you are logged in and on the Suno 'Create' page.");
  }
})();`;

export function SunoSyncEngine({ sunoCookie, onSave }: SunoSyncEngineProps): React.ReactElement {
  const { save: saveUserSettings } = useUserSettings();
  const [localSunoCookie, setLocalSunoCookie] = useState(sunoCookie);
  const [storedToken, setStoredToken] = useState<{
    hasToken: boolean;
    capturedAt?: string;
    expiresAt?: string | null;
    isExpired?: boolean;
    autoRefreshEnabled?: boolean;
    refreshHealth?: { ok: boolean; lastAttemptAt: string; lastSuccessAt: string | null; lastError: string | null } | null;
  } | null>(null);
  const [loadingStored, setLoadingStored] = useState(false);
  const [localSunoProxy, setLocalSunoProxy] = useState('');
  const [localSunoModel, setLocalSunoModel] = useState('chirp-hawk');
  const [sunoStatus, setSunoStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [sunoErrorMessage, setSunoErrorMessage] = useState('');
  const [credits, setCredits] = useState<number | null>(null);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [tokenExpiresAt, setTokenExpiresAt] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>('00');

  // Parse JWT to get expiry time
  function parseJWTExpiry(token: string): Date | null {
    try {
      if (!token || !token.startsWith('eyJ')) return null;
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      return payload.exp ? new Date(payload.exp * 1000) : null;
    } catch {
      return null;
    }
  }

  // Format time remaining
  function formatTimeRemaining(expiresAt: Date | null): string {
    if (!expiresAt) return '00';
    const now = new Date();
    const diff = expiresAt.getTime() - now.getTime();
    if (diff <= 0) return '00';
    const minutes = Math.floor(diff / 60000);
    return minutes.toString().padStart(2, '0');
  }

  useEffect(() => {
    setLocalSunoCookie(localStorage.getItem('suno_cookie') || '');
    setLocalSunoProxy(localStorage.getItem('suno_proxy_url') || '');
    setLocalSunoModel(resolveSunoGenerationModel(localStorage.getItem('suno_model')));
  }, []);

  // Parse token expiry when token changes
  useEffect(() => {
    const expiresAt = parseJWTExpiry(localSunoCookie);
    setTokenExpiresAt(expiresAt);
    setTimeRemaining(formatTimeRemaining(expiresAt));
  }, [localSunoCookie]);

  // Update timer every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(tokenExpiresAt));
    }, 60000);
    return () => clearInterval(interval);
  }, [tokenExpiresAt]);

  // Check if a stored token exists on the server (captured via suno-session-capture.mjs),
  // and whether the server-side keep-alive loop is actively refreshing it. Poll periodically
  // since the token in the background can change without any action here.
  useEffect(() => {
    const check = () => {
      fetchWithCsrf('/api/suno/stored-token')
        .then(r => r.json())
        .then(setStoredToken)
        .catch(() => setStoredToken(null));
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLoadStoredToken = useCallback(async () => {
    setLoadingStored(true);
    try {
      const r = await fetchWithCsrf('/api/suno/load-stored-token');
      if (!r.ok) { setLoadingStored(false); return; }
      const { token } = await r.json();
      setLocalSunoCookie(token);
    } finally {
      setLoadingStored(false);
    }
  }, []);

  function handleCopyScript(): void {
    copyToClipboard(EXTRACTION_SCRIPT);
    setScriptCopied(true);
    setTimeout(() => setScriptCopied(false), 2000);
  }

  async function handleVerifySuno(): Promise<void> {
    if (!localSunoCookie) return;
    setSunoStatus('verifying');
    setSunoErrorMessage('');
    try {
      localStorage.setItem('suno_proxy_url', localSunoProxy);
      const creditInfo = await getSunoCredits(localSunoCookie);
      setCredits(creditInfo.total_credits_left);
      setSunoStatus('success');
      // Persist to DB (via useUserSettings.save) — this is the fix for the
      // "JWT refresh reverts my model" bug. Without DB persistence, the next
      // useUserSettings fetch overwrites localStorage with the stale DB row.
      localStorage.setItem('suno_cookie', localSunoCookie);
      localStorage.setItem('suno_model', localSunoModel);
      await saveUserSettings({
        suno_cookie: localSunoCookie,
        suno_proxy_url: localSunoProxy,
        suno_model: localSunoModel,
      });
      onSave({ sunoCookie: localSunoCookie, sunoProxyUrl: localSunoProxy });
    } catch (err: unknown) {
      setSunoStatus('error');
      setSunoErrorMessage(err instanceof Error ? err.message : 'Verification failed.');
    }
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2 text-accent">
        <ShieldAlert size={18} />
        <h3 className="text-sm font-black uppercase tracking-widest">Suno Sync Engine</h3>
      </div>

      {/* Auto-Capture card — only shown when the script has been run at least once */}
      <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Terminal size={13} className="text-accent" />
            <span className="text-[11px] font-black text-accent uppercase tracking-widest">Auto-Capture</span>
          </div>
          {storedToken?.hasToken && !storedToken.isExpired && (
            <Button
              variant="secondary"
              size="xs"
              onClick={handleLoadStoredToken}
              isLoading={loadingStored}
              leftIcon={<CloudDownload size={11} />}
              className="text-[11px] font-black uppercase tracking-widest"
            >
              Load into field
            </Button>
          )}
        </div>

        {storedToken === null && (
          <p className="text-[11px] text-slate-500 italic">Checking server…</p>
        )}
        {storedToken?.hasToken === false && (
          <p className="text-[11px] text-slate-500 italic">No token captured yet. Run the script below on Optimus.</p>
        )}
        {storedToken?.hasToken && (
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant={storedToken.isExpired ? 'danger-muted' : 'success-muted'}
                size="xs"
              >
                {storedToken.isExpired ? 'Expired' : 'Ready'}
              </Badge>
              {storedToken.autoRefreshEnabled ? (
                <Badge variant="success-muted" size="xs" leftIcon={<RefreshCw size={10} />}>
                  Keep-Alive Active
                </Badge>
              ) : (
                <Badge variant="default-muted" size="xs">
                  Manual token — no keep-alive
                </Badge>
              )}
              {storedToken.capturedAt && (
                <span className="text-[11px] text-slate-500">
                  Captured {new Date(storedToken.capturedAt).toLocaleString()}
                </span>
              )}
            </div>
            {storedToken.isExpired && !storedToken.autoRefreshEnabled && (
              <p className="text-[11px] text-red-400 italic">Token expired — re-run the script to refresh it.</p>
            )}
            {!storedToken.autoRefreshEnabled && !storedToken.refreshHealth && (
              <p className="text-[11px] text-slate-500 italic">Re-run the script once to enable Keep-Alive — the server will then refresh the token automatically and this stops mattering.</p>
            )}
            {!storedToken.autoRefreshEnabled && storedToken.refreshHealth && !storedToken.refreshHealth.ok && (
              <p className="text-[11px] text-red-400 italic">Keep-Alive is failing: {storedToken.refreshHealth.lastError ?? 'unknown error'}</p>
            )}
          </div>
        )}

        <div className="bg-slate-900/60 rounded-lg p-2.5 font-mono text-[10px] text-slate-400 leading-relaxed select-all">
          <span className="text-slate-600">$ </span>
          SUNO_CAPTURE_SECRET=&lt;from .env&gt; node scripts/suno-session-capture.mjs
        </div>
        <p className="text-[10px] text-slate-600 leading-relaxed">Run this once in a terminal on Optimus (the dev machine) — it opens Chrome, logs into Suno, and pushes the session here. If Keep-Alive activates, the server refreshes the token itself from then on and you shouldn't need to run this again.</p>
      </div>

      <div className="bg-[var(--color-slate-900)] border-l-4 border-l-accent rounded-lg p-5 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <h4 className="text-[11px] font-black text-accent uppercase tracking-widest">Get Bearer Token (JWT)</h4>
          <div className="flex items-center gap-2">
            <Badge
              variant={timeRemaining === '00' ? 'danger-muted' : 'success-muted'}
              size="xs"
              leftIcon={<Clock size={10} />}
            >
              {timeRemaining}m
            </Badge>
            <Button
              variant={scriptCopied ? 'success' : 'secondary'}
              size="xs"
              leftIcon={scriptCopied ? <Check size={12} /> : <Copy size={12} />}
              onClick={handleCopyScript}
              className="text-[11px] font-black uppercase tracking-widest"
            >
              {scriptCopied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>

        <div className="text-[11px] text-slate-400 space-y-3 leading-relaxed border-b border-slate-800 pb-4">
          <p>Browsers block session cookies. Use the **Bearer Token (JWT)** from your Suno account for direct architecture sync.</p>
          <ol className="list-decimal list-inside space-y-1.5 ml-1 font-medium">
            <li>Log in at <a href="https://suno.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">suno.com</a>.</li>
            <li>Paste the extraction script (above) into your F12 Console.</li>
            <li>Paste the result in the field below.</li>
          </ol>
        </div>

        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Suno Bearer Token (JWT)</label>
          <TextArea
            value={localSunoCookie}
            onChange={(e) => setLocalSunoCookie(e.target.value)}
            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
            className="bg-[var(--color-slate-950)] border-slate-800 rounded-xl text-[11px] font-mono h-28 resize-none shadow-inner"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 ml-1">
            <Globe size={12} className="text-slate-400" />
            <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Suno Proxy URL</label>
          </div>
          <Input
            type="text"
            value={localSunoProxy}
            onChange={(e) => setLocalSunoProxy(e.target.value)}
            placeholder="https://your-suno-proxy.workers.dev"
            className="bg-[var(--color-slate-950)] border-slate-800 rounded-xl text-[11px] shadow-inner"
          />
        </div>

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            {sunoStatus === 'success' && (
              <Badge variant="success-muted" size="sm" leftIcon={<CheckCircle size={14} />}>
                Connected: {credits} Credits
              </Badge>
            )}
            {sunoStatus === 'verifying' && (
              <Badge variant="default-muted" size="sm" leftIcon={<RefreshCw size={14} className="animate-spin" />}>
                Authenticating...
              </Badge>
            )}
            {sunoStatus === 'error' && (
              <Badge variant="danger-muted" size="sm" leftIcon={<AlertTriangle size={14} />}>
                Link Failed
              </Badge>
            )}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleVerifySuno}
            disabled={!localSunoCookie}
            isLoading={sunoStatus === 'verifying'}
            loadingText="Testing..."
            className="font-black text-[11px] uppercase tracking-widest"
          >
            Test Connection
          </Button>
        </div>
        {sunoStatus === 'error' && <p className="text-[11px] text-red-400 font-bold px-1 italic">{sunoErrorMessage}</p>}
      </div>

      <div className="space-y-1.5">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest ml-1">Generation Model</label>
        <Select
          value={localSunoModel}
          onChange={(e) => {
            const next = e.target.value;
            setLocalSunoModel(next);
            localStorage.setItem('suno_model', next);
            // Persist to DB so it survives future settings syncs
            void saveUserSettings({ suno_model: next });
          }}
          className="bg-[var(--color-slate-950)] border-slate-800 rounded-xl text-xs"
          options={SUNO_GENERATION_MODELS.map(({ value, label }) => ({ value, label }))}
        />
      </div>

      <SunoModelsPanel cookie={localSunoCookie} />
    </section>
  );
}
